import { createContext, useMemo } from 'react';

import { useSafeContext } from '@aspendental/shared-utils/react';

import { TAGBrand, BrandThemeConfig } from '@/types';
import { default as baseConfig } from '@/theme/BaseBrandThemeConfig';
import { httpClient } from '@/utils/httpClient/httpClient';

export interface IAppContext {
	theme: TAGBrand;
	environment: EnvironmentType;
	config: BrandThemeConfig;
	appName: string;
	appVersion: string;
	dentrinoClientId?: string;
	dentrinoClientSecret?: string;
	stateCodeFromHeaders?: string;
}
export interface IAppContextProvider extends IAppContext {
	children: React.ReactNode;
}

const AppContext = createContext<IAppContext>({
	theme: 'AspenDental' as TAGBrand,
	environment: 'prod' as EnvironmentType,
	config: baseConfig,
	appName: 'tag_unknown',
	appVersion: 'unknown',
	dentrinoClientId: 'unknown',
	dentrinoClientSecret: 'unknown',
	stateCodeFromHeaders: '',
});
AppContext.displayName = 'AppContext';

type EnvironmentType = 'local' | 'dev' | 'stage' | 'prod';

export function AppContextProvider({
	theme,
	environment,
	config,
	appName,
	appVersion,
	children,
	dentrinoClientId,
	dentrinoClientSecret,
	stateCodeFromHeaders,
}: Readonly<IAppContextProvider>) {
	const appContext = useMemo(
		() => ({
			theme: theme,
			environment: environment,
			config: config,
			appName: appName,
			appVersion: appVersion,
			dentrinoClientId: dentrinoClientId ?? '',
			dentrinoClientSecret: dentrinoClientSecret ?? '',
			stateCodeFromHeaders: stateCodeFromHeaders ?? '',
		}),
		[theme, environment, config, appName, appVersion, dentrinoClientId, dentrinoClientSecret, stateCodeFromHeaders]
	);
	const revspringApiUrl =
		environment === 'prod' ? config.services.prod.REVSPRING_API_URL : config.services.nonprod.REVSPRING_API_URL;

	// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
	httpClient.getHTTPClient().defaults.baseURL = revspringApiUrl;
	return <AppContext.Provider value={appContext}>{children}</AppContext.Provider>;
}

export function useAppContext() {
	return useSafeContext(AppContext);
}
